/**
 * Image Compression Utility
 * Compresses images to a target size while maintaining aspect ratio
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  maxSizeMB?: number;
  quality?: number;
  fileType?: string;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1200,
  maxHeight: 1200,
  maxSizeMB: 2,
  quality: 0.8,
  fileType: 'image/jpeg',
};

/**
 * Compresses an image file to meet size requirements
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<File> - The compressed image file
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<File> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { maxWidth, maxHeight, maxSizeMB, quality, fileType } = opts;

  // If file is already small enough, return it
  const maxBytes = (maxSizeMB || 2) * 1024 * 1024;
  if (file.size <= maxBytes && !needsResize(file, maxWidth!, maxHeight!)) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > maxWidth! || height > maxHeight!) {
            const ratio = Math.min(maxWidth! / width, maxHeight! / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Draw image on canvas
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to create blob'));
                return;
              }

              // If still too large, reduce quality further
              if (blob.size > maxBytes) {
                compressFurther(canvas, maxBytes, fileType!, resolve, reject, file.name);
              } else {
                const compressedFile = new File([blob], getFileName(file.name, fileType!), {
                  type: fileType!,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              }
            },
            fileType!,
            quality
          );
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Check if image needs resizing
 */
const needsResize = (file: File, maxWidth: number, maxHeight: number): boolean => {
  return new Promise<boolean>((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(img.width > maxWidth || img.height > maxHeight);
    };
    img.onerror = () => resolve(false);
    img.src = URL.createObjectURL(file);
  }) as unknown as boolean;
};

/**
 * Further compress image by reducing quality incrementally
 */
const compressFurther = (
  canvas: HTMLCanvasElement,
  maxBytes: number,
  fileType: string,
  resolve: (file: File) => void,
  reject: (error: Error) => void,
  originalName: string,
  currentQuality: number = 0.7
) => {
  if (currentQuality < 0.1) {
    // If we've reduced quality too much, resize the image
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    // Reduce dimensions by 50%
    const newWidth = Math.round(canvas.width * 0.5);
    const newHeight = Math.round(canvas.height * 0.5);
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = newWidth;
    tempCanvas.height = newHeight;
    const tempCtx = tempCanvas.getContext('2d');
    
    if (!tempCtx) {
      reject(new Error('Could not get temp canvas context'));
      return;
    }

    tempCtx.drawImage(canvas, 0, 0, newWidth, newHeight);
    
    tempCanvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        const compressedFile = new File([blob], getFileName(originalName, fileType), {
          type: fileType,
          lastModified: Date.now(),
        });
        resolve(compressedFile);
      },
      fileType,
      0.8
    );
    return;
  }

  canvas.toBlob(
    (blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob'));
        return;
      }

      if (blob.size <= maxBytes) {
        const compressedFile = new File([blob], getFileName(originalName, fileType), {
          type: fileType,
          lastModified: Date.now(),
        });
        resolve(compressedFile);
      } else {
        compressFurther(canvas, maxBytes, fileType, resolve, reject, originalName, currentQuality - 0.1);
      }
    },
    fileType,
    currentQuality
  );
};

/**
 * Get file name with correct extension
 */
const getFileName = (originalName: string, fileType: string): string => {
  const baseName = originalName.replace(/\.[^/.]+$/, '');
  const extension = fileType === 'image/png' ? 'png' : 'jpg';
  return `${baseName}.${extension}`;
};

/**
 * Validate image file type
 */
export const isValidImageType = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * Get image preview URL
 */
export const getImagePreview = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Revoke image preview URL to free memory
 */
export const revokeImagePreview = (url: string): void => {
  URL.revokeObjectURL(url);
};
